import { z } from "zod";
import { UF_OPTIONS } from "../../utils/utils";

export const FormSchema = z
  .object({
    state: z.string({
      required_error: "Estado é obrigatório",
    }),
    rule_name: z
      .string({ required_error: "Nome da regra é obrigatório" })
      .min(1, "Nome da regra é obrigatório"),
    cst: z
      .string({ required_error: "CST é obrigatório." })
      .nonempty("CST é obrigatório.")
      .length(3, "CST deve ter 3 caracteres."),
    cfop: z
      .string()
      .optional()
      .refine((val) => !val || val.length === 4, {
        message: "CFOP deve ter 4 caracteres.",
      }),
    classification_tax: z
      .string()
      .optional()
      .refine((val) => !val || val.length === 6, {
        message: "Classificação Tributária deve ter 6 caracteres.",
      }),
    calculation_basis: z.string({
      required_error: "Base de cálculo é obrigatória",
    }),
    cest: z
      .string()
      .optional()
      .refine((val) => !val || val.length === 7, {
        message: "CEST deve ter exatamente 7 caracteres",
      }),
    exception_rate: z.string({
      required_error: "Taxa de exceção é obrigatória",
    }),
    description: z
      .string({ required_error: "Descrição é obrigatória" })
      .min(1, "Descrição é obrigatória"),
    initial_term: z
      .string({ required_error: "Data inicial é obrigatória" })
      .nonempty("Data inicial é obrigatória"),

    final_term: z.string().optional(),
    exception_type: z.string({
      required_error: "Tipo de exceção é obrigatória",
    }),
    reason: z.string().optional(),
  })
  .refine(
    (data) => {
      return data.state === "TODOS" || UF_OPTIONS.includes(data.state);
    },
    {
      message: "Estado é obrigatório",
      path: ["state"],
    }
  )
  .refine(
    (data) => {
      if (
        (data.exception_type === "ICMS" ||
          data.exception_type === "ICMS_DESONERADO") &&
        !data.cfop
      ) {
        return false;
      }
      return true;
    },
    {
      message: "CFOP é obrigatório para ICMS e ICMS DESONERADO",
      path: ["cfop"],
    }
  )
  .refine(
    (data) => {
      if (
        (data.exception_type === "IBS" || data.exception_type === "CBS") &&
        !data.classification_tax
      ) {
        return false;
      }
      return true;
    },
    {
      message: "Classificação Tributária é obrigatória para IBS e CBS",
      path: ["classification_tax"],
    }
  )
  .refine(
    (data) => {
      if (data.exception_type === "ICMS_DESONERADO" && !data.reason) {
        return false;
      }
      return true;
    },
    {
      message: "O Motivo é obrigatório",
      path: ["reason"],
    }
  )
  .superRefine((data, ctx) => {
    const initial = new Date(data.initial_term);
    const final = data.final_term ? new Date(data.final_term) : null;

    const issues = [
      !(initial instanceof Date && initial.toString() !== "Invalid Date") && {
        path: ["initial_term"],
        code: "custom",
        message: "Data inicial inválida",
      },
      final &&
        !(final instanceof Date && final.toString() !== "Invalid Date") && {
          path: ["final_term"],
          code: "custom",
          message: "Data final inválida",
        },
      final &&
        initial instanceof Date &&
        final instanceof Date &&
        initial.toString() !== "Invalid Date" &&
        final.toString() !== "Invalid Date" &&
        final < initial && {
          path: ["final_term"],
          code: "custom",
          message: "Data final deve ser posterior à inicial",
        },
    ].filter(Boolean) as z.ZodIssue[];

    issues.forEach((issue) => ctx.addIssue(issue));
  });

export const CreateModalSchema = z
  .object({
    ncm: z.string().optional(),
    sku: z.string().optional(),
    description: z
      .string({ required_error: "Descrição é obrigatória" })
      .nonempty("Descrição é obrigatória"),
    type: z.enum(["NCM", "Produto"]),
  })
  .refine(
    (data) => {
      if (data.type === "NCM") {
        return !!data.ncm?.trim();
      }
      return true;
    },
    {
      message: "NCM é obrigatório",
      path: ["ncm"],
    }
  )
  .refine(
    (data) => {
      if (data.type === "NCM" && data.ncm && data.ncm !== "TODOS") {
        const cleanNcm = data.ncm.replace(/\D/g, "");
        return cleanNcm.length === 8;
      }
      return true;
    },
    {
      message: "O NCM deve conter exatamente 8 dígitos",
      path: ["ncm"],
    }
  )
  .refine(
    (data) => {
      if (data.type === "Produto") {
        return !!data.sku?.trim();
      }
      return true;
    },
    {
      message: "SKU é obrigatório",
      path: ["sku"],
    }
  );
